import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type UIEvent } from "react"
import type { SignalCategory, SignalType } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import { localizeSignalTitle } from "../i18n/signalTitles"
import { exactFigmaSignalColor, FigmaSignalGlyph, hasExactFigmaSignalGlyph } from "./FigmaSignalGlyphLibrary"
import "./SignalSelectionSheet.css"

const SIGNAL_CATEGORIES = [
  { key: "attention", messageKey: "signalSelection.category.attention" },
  { key: "road", messageKey: "signalSelection.category.road" },
  { key: "urgent", messageKey: "signalSelection.category.urgent" },
] as const satisfies ReadonlyArray<{ key: SignalCategory; messageKey: "signalSelection.category.attention" | "signalSelection.category.road" | "signalSelection.category.urgent" }>

interface SignalSelectionSheetProps {
  selectedCategory: SignalCategory
  signals: SignalType[]
  selectedSignal: SignalType | null
  onCategoryChange: (category: SignalCategory) => void
  onSignalChange: (signal: SignalType) => void
  onSuggestOwn: () => void
  onBack: () => void
  onNext: () => void
}

/** One runtime sheet for all Android/iOS, scroll, and selection Figma variants. */
export function SignalSelectionSheet({
  selectedCategory,
  signals,
  selectedSignal,
  onCategoryChange,
  onSignalChange,
  onSuggestOwn,
  onBack,
  onNext,
}: SignalSelectionSheetProps) {
  const { locale, direction, t } = useI18n()
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [hasMoreBelow, setHasMoreBelow] = useState(true)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const updateScrollState = useCallback((element: HTMLDivElement) => {
    setIsScrolled(element.scrollTop > 0)
    setHasMoreBelow(element.scrollTop + element.clientHeight < element.scrollHeight - 1)
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const figmaScrolledState = import.meta.env.DEV
      && new URLSearchParams(window.location.search).get("signalScroll") === "scrolled"
    viewport.scrollTop = figmaScrolledState ? 58 : 0
    updateScrollState(viewport)
  }, [selectedCategory, signals, updateScrollState])

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    updateScrollState(event.currentTarget)
  }

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const handleGrabberPointerDown = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleGrabberPointerMove = (event: ReactPointerEvent<HTMLSpanElement>) => {
    if (dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLSpanElement>, allowClose: boolean) => {
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) onBack()
  }

  return (
    <section
      className={`bbf-signal-selection${isDragging ? " is-dragging" : ""}`}
      style={{ "--bb-signal-selection-drag-y": `${dragOffset}px` } as CSSProperties}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bbf-signal-selection-title"
    >
      <span
        className="bbf-signal-selection-grabber"
        aria-hidden="true"
        onPointerDown={handleGrabberPointerDown}
        onPointerMove={handleGrabberPointerMove}
        onPointerUp={(event) => finishGrabberDrag(event, true)}
        onPointerCancel={(event) => finishGrabberDrag(event, false)}
      />
      <p id="bbf-signal-selection-title" className="bbf-signal-selection-heading type-heading" dir={direction}>{t("signalSelection.chooseType")}</p>
      <div className="bbf-signal-selection-categories">
        {SIGNAL_CATEGORIES.map((category) => (
          <button
            type="button"
            key={category.key}
            className={`category-${category.key}${selectedCategory === category.key ? " active" : ""}`}
            aria-pressed={selectedCategory === category.key}
            onClick={() => onCategoryChange(category.key)}
            dir={direction}
          >
            {t(category.messageKey)}
          </button>
        ))}
      </div>
      <p className="bbf-signal-selection-heading signal-heading" dir={direction}>{t("signalSelection.chooseSignal")}</p>
      <div ref={viewportRef} className="bbf-signal-selection-viewport" onScroll={handleScroll}>
        <div className="bbf-signal-selection-list">
          {signals.map((signal) => {
            const hasGlyph = hasExactFigmaSignalGlyph(signal.category, signal.slug)
            const exactTone = exactFigmaSignalColor(signal.category, signal.slug)
            const isSelected = selectedSignal?.id === signal.id
            const localizedTitle = localizeSignalTitle(locale, signal.title)
            if (!hasGlyph && import.meta.env.DEV) {
              console.warn(`[Signal Selection] Нет final SVG для ${signal.category}/${signal.slug}`)
            }
            return (
              <button
                type="button"
                key={signal.id}
                className={`category-${signal.category}${isSelected ? " selected" : ""}`}
                aria-pressed={isSelected}
                style={exactTone ? ({ "--bb-signal-selection-tone": exactTone } as CSSProperties) : undefined}
                onClick={() => onSignalChange({ ...signal, title: localizedTitle })}
              >
                <span className="bbf-signal-selection-icon" aria-hidden="true">
                  {hasGlyph && <FigmaSignalGlyph category={signal.category} slug={signal.slug} />}
                </span>
                <span className="bbf-signal-selection-label" dir={direction}>{localizedTitle}</span>
              </button>
            )
          })}
          <button
            type="button"
            className={`category-${selectedCategory} suggest-own`}
            onClick={onSuggestOwn}
          >
            <span className="bbf-signal-selection-icon" aria-hidden="true">
              <FigmaSignalGlyph category={selectedCategory} slug="predlozhit-svoy-variant" />
            </span>
            <span className="bbf-signal-selection-label" dir={direction}>{t("signalSelection.suggestOwn")}</span>
          </button>
        </div>
      </div>
      {isScrolled && <span className="bbf-signal-selection-fade top" aria-hidden="true" />}
      {hasMoreBelow && <span className="bbf-signal-selection-fade bottom" aria-hidden="true" />}
      <div className="bbf-signal-selection-actions">
        <button type="button" onClick={onBack} dir={direction}>{t("common.back")}</button>
        <button type="button" className="next" onClick={onNext} disabled={!selectedSignal} dir={direction}>{t("common.next")}</button>
      </div>
    </section>
  )
}
